import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import axios from 'axios';
import './Login.css';
import { useNavigate } from 'react-router-dom';
import google from '../../Assets/google.png';
import phoneImg from '../../Assets/p91.png';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import AuthLayout from '../shared/AuthLayout';
import { roundedFieldSx, roundedOtpFieldSx } from '../shared/authFieldStyles';

const Login = () => {
  const [email, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(Array(4).fill(''));
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const [LoginMethod, setLoginMethod] = useState('email');
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(60);
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  useEffect(() => {
    if (otpSent && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [otpSent, timer]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (LoginMethod === 'email') {
        const response = await axios.post('http://127.0.0.1:8000/login/user', {
          email,
          password,
        });

        const { access, refresh, user_id } = response.data;

        localStorage.setItem('access_token', access);
        localStorage.setItem('refresh_token', refresh);
        localStorage.setItem('user_id', user_id);
        localStorage.setItem('username', email);

        setSuccess('Login successful! Redirecting...');

        setTimeout(() => {
          navigate('/dashboard');
        }, 500);
      } else if (LoginMethod === 'phone' && otpSent) {
        const otpCode = otp.join('');
        console.log('Verifying OTP:', otpCode);
        setSuccess('OTP verified successfully!');
      } else if (LoginMethod === 'phone') {
        console.log('Requesting OTP for phone:', phone);
        setOtpSent(true);
        setSuccess('OTP sent to your phone number!');
        setTimer(60);
      }
    } catch (err) {
      console.error('Error during login:', err);
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    }
  };

  const handleMethodChange = (method) => {
    setLoginMethod(method);
    setUsername('');
    setPassword('');
    setPhone('');
    setOtp(Array(4).fill(''));
    setOtpSent(false);
    setError('');
    setSuccess('');
    setTimer(60);
  };
  const handleOtpChange = (index, value) => {
    const updatedOtp = [...otp];
    updatedOtp[index] = value.slice(-1);
    setOtp(updatedOtp);
  };
  const resendOtp = () => {
    setSuccess('OTP resent to your phone number!');
    setTimer(60);
  };

  return (
    <AuthLayout
      heading="Welcome back!"
      subheading="Log in to keep tracking your bills and services."
      footerPrompt="Don't have an account?"
      footerLinkLabel="Sign up"
      footerLinkTo="/SignupPage"
    >
      <form onSubmit={handleLogin} style={{ width: '100%' }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            width: '100%',
            borderRadius: '30px',
            overflow: 'hidden',
            background: '#F2F6FF',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            mb: 2,
          }}
        >
          <Box
            sx={{
              flex: 1,
              padding: '10px',
              textAlign: 'center',
              cursor: 'pointer',
              borderRadius: '30px',
              backgroundColor: LoginMethod === 'email' ? 'white' : '#F2F6FF',
              color: LoginMethod === 'email' ? 'black' : 'grey',
              fontSize: 13,
              fontWeight: 600,
              transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
            }}
            onClick={() => handleMethodChange('email')}
          >
            Login with Email
          </Box>
          <Box
            sx={{
              flex: 1,
              padding: '10px',
              textAlign: 'center',
              cursor: 'pointer',
              borderRadius: '30px',
              backgroundColor: LoginMethod === 'phone' ? 'white' : '#F2F6FF',
              color: LoginMethod === 'phone' ? 'black' : 'grey',
              fontSize: 13,
              fontWeight: 600,
              transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
            }}
            onClick={() => handleMethodChange('phone')}
          >
            Login with Phone
          </Box>
        </Box>

        {LoginMethod === 'email' ? (
          <>
            <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mt: 1 }}>
              Email
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              required
              margin="normal"
              value={email}
              onChange={(e) => setUsername(e.target.value)}
              sx={roundedFieldSx}
            />
            <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mt: 1 }}>
              Password
            </Typography>
            <TextField
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              margin="normal"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={roundedFieldSx}
            />
          </>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mt: 1 }}>
              Enter mobile number
            </Typography>
            <TextField
              type="tel"
              variant="outlined"
              fullWidth
              margin="normal"
              placeholder="Enter your number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Box component="img" src={phoneImg} alt="" aria-hidden="true" sx={{ mr: 1, width: 24, height: 24 }} />
                ),
              }}
              sx={roundedFieldSx}
            />

            {otpSent && (
              <>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 1.5 }}>
                  Didn't receive the code?{' '}
                  {timer > 0 ? (
                    <span>Resend in {timer}s</span>
                  ) : (
                    <Link component="button" type="button" onClick={resendOtp} underline="hover">
                      Resend OTP
                    </Link>
                  )}
                </Typography>
                <Box display="flex" justifyContent="center" gap={1} mt={1.5}>
                  {otp.map((digit, index) => (
                    <TextField
                      key={index}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
                      sx={roundedOtpFieldSx}
                    />
                  ))}
                </Box>
              </>
            )}
          </>
        )}

        <Box mt={2}>
          {error && <Typography color="error" sx={{ fontSize: 13.5 }}>{error}</Typography>}
          {success && <Typography color="success.main" sx={{ fontSize: 13.5 }}>{success}</Typography>}
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{ mt: 2.5, borderRadius: 999, py: 1.2, fontWeight: 600, textTransform: 'none', boxShadow: 'none' }}
        >
          {LoginMethod === 'email' ? 'Log In' : otpSent ? 'Verify OTP' : 'Request OTP'}
        </Button>

        <Button
          startIcon={<Box component="img" src={google} alt="" aria-hidden="true" sx={{ width: 20, height: 20 }} />}
          fullWidth
          sx={{
            mt: 1.5,
            borderRadius: 999,
            py: 1.2,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            '&:hover': { boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)' },
          }}
        >
          Sign in with Google
        </Button>
      </form>
    </AuthLayout>
  );
};

export default Login;
